'use client'

import React, { useTransition } from 'react'
import { useFormStatus } from 'react-dom'
import { toast } from 'sonner'
import {
  closeRegister,
  getRegisterSummary,
  setCookieCartId,
} from '@/lib/actions'
import { useApp, useCloseRegisterPrinter, useModals } from '@/lib/hooks'
import { cn, formatPrice } from '@/lib/utils'

import {
  Badge,
  Button,
  InputForm,
  LoadingDots,
  ScrollArea,
} from '@/components/ui'

interface CloseRegisterProps extends React.ComponentPropsWithoutRef<'div'> {}

export const CloseRegister = ({ className }: CloseRegisterProps) => {
  const { requestPrinter, printer, printReceipt } = useCloseRegisterPrinter()
  const { closeAll } = useModals()
  const [isPendingSummary, startSummaryTransition] = useTransition()
  const [summary, setSummary] = React.useState([])

  const [totalTransactions, setTotalTransactions] = React.useState<any>(null)

  const [fullSummary, setFullSummary] = React.useState<any>(null)
  const { signOut } = useApp()
  const [closingBalance, setClosingBalance] = React.useState(0)

  const getSummary = React.useCallback(async () => {
    startSummaryTransition(async () => {
      const res = await getRegisterSummary()
      setSummary(res?.summary)
      setFullSummary(res)
      setTotalTransactions(res?.total_transactions_amount?.[0])
    })
  }, [])

  React.useEffect(() => {
    getSummary()
  }, [getSummary])

  const handleRegister = async (closingBalance: any) => {
    try {
      await setCookieCartId('')
      await closeRegister(closingBalance)

      try {
        // await printReceipt({ summary, fullSummary, closingBalance })
        // Only sign out if printing succeeds
        closeAll(() => signOut())
      } catch (error) {
        toast.error(`Couldn't print receipt. Please check printer connection.`)
        return
      }
    } catch (error) {
      toast.error(`Couldn't close register.`)
    }
  }

  const handleSignOut = async (data: FormData) => {
    const closingBalance = data.get('closing-balance')
    await handleRegister(closingBalance)
  }

  const printNow = async () => {
    await printReceipt({ summary, fullSummary, closingBalance })
  }

  return (
    <form
      action={handleSignOut}
      className={cn('flex w-full flex-col gap-5', className)}
    >
      <div className="flex flex-col gap-2">
        <InputForm
          autoCorrect="off"
          autoComplete="off"
          maxLength={4}
          name="closing-balance"
          placeholder="10,000.00"
          className="lg:h-[44px]"
          icon={<span className="font-mono font-bold">Rs.</span>}
          type="number"
          onChangeValue={(value) => setClosingBalance(Number(value))}
        />
      </div>

      {isPendingSummary && (
        <div className="flex h-[300px] w-full items-center justify-center">
          <LoadingDots />
        </div>
      )}

      {!isPendingSummary && summary?.length === 0 && (
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg border border-gray-100 text-sm text-gray-700">
          No transactions found.
        </div>
      )}

      {summary && summary?.length !== 0 && (
        <ScrollArea className="h-[300px]">
          <div className="flex w-full flex-col gap-1 pr-4 lg:pr-[unset]">
            {summary?.map(
              (
                {
                  payment_method,
                  currency,
                  transaction_total,
                  transaction_count,
                },
                index: number,
              ) => {
                return (
                  <div
                    key={index}
                    className={cn(
                      'group flex w-full select-none items-center justify-between gap-4 rounded-md border border-gray-100 border-gray-100/80 bg-white px-3 py-2 shadow-card transition-colors dark:border-gray-700 hover:dark:border-gray-700',
                      className,
                    )}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {payment_method}
                        </span>
                        <Badge>{currency}</Badge>
                      </div>
                      <span className="text-xs text-gray-700">
                        {transaction_count} Transaction
                        {transaction_count !== 1 && 's'}
                      </span>
                    </div>

                    <div className="flex items-center gap-0.5">
                      <span className="font-mono">
                        {formatPrice(transaction_total, 2, false)}
                      </span>
                      <span className="font-mono">{currency}</span>
                    </div>
                  </div>
                )
              },
            )}

            <div
              className={cn(
                'group flex w-full select-none items-center justify-between gap-4 rounded-md border border-gray-100 border-gray-100/80 bg-white px-3 py-2 shadow-card transition-colors dark:border-gray-700 hover:dark:border-gray-700',
                className,
              )}
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    Total Transactions
                  </span>
                  <Badge>{totalTransactions?.currency}</Badge>
                </div>
                <span className="text-xs text-gray-700">
                  {totalTransactions?.transaction_count} Transaction
                  {totalTransactions?.transaction_count !== 1 && 's'}
                </span>
              </div>

              <div className="flex items-center gap-0.5">
                <span className="font-mono">
                  {formatPrice(totalTransactions?.transaction_total, 2, false)}
                </span>
                <span className="font-mono">{totalTransactions?.currency}</span>
              </div>
            </div>
          </div>
        </ScrollArea>
      )}

      <Actions
        requestPrinter={requestPrinter}
        printer={printer}
        printReceipt={printReceipt}
        printNow={printNow}
      />
    </form>
  )
}

const Actions = ({ requestPrinter, printer, printNow }: any) => {
  const { pending } = useFormStatus()
  return (
    <div className="flex w-full items-center gap-2">
      {printer && (
        <Button
          variant="outline"
          className="w-full"
          size="lg"
          disabled={pending}
          onClick={printNow}
        >
          Print
        </Button>
      )}

      {!printer && (
        <Button
          variant="outline"
          className="w-full"
          size="lg"
          disabled={pending}
          onClick={requestPrinter}
        >
          Print
        </Button>
      )}

      <Button type="submit" className="w-full" size="lg" disabled={pending}>
        {pending ? 'Closing' : 'Close Register'}
      </Button>
    </div>
  )
}
